import {Component, EventEmitter, forwardRef, Inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormControl, NG_VALUE_ACCESSOR} from "@angular/forms";
import {Ofwca} from "../../interface/ofwca";
import {ReplaySubject} from "rxjs";
import {DOCUMENT} from "@angular/common";
import {OfwcaService} from "../../services/ofwca.service";
import {FileManagerPageService} from "../../services/file-manager-page.service";
import {PageManager} from "../../interface/page-manager";
import {ListResponse} from "../../interface/response/list-response";
import {MatSelectChange} from "@angular/material/select";
import {debounceTime, distinctUntilChanged, startWith, takeUntil} from "rxjs/operators";

@Component({
  selector: 'app-dict-page-file-manager',
  templateUrl: './dict-page-file-manager.component.html',
  styleUrls: ['./dict-page-file-manager.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => DictPageFileManagerComponent)}
  ]
})
export class DictPageFileManagerComponent implements OnInit, OnDestroy, ControlValueAccessor {

  @Input() label: string;
  @Input() multiple = false;
  @Input() control: FormControl = null;
  @Input() required = false;
  @Output() selectedPage: EventEmitter<PageManager> = new EventEmitter<PageManager>();

  externalControl = true;
  disabled = false;
  items: PageManager[] = [];
  value?: string = null;
  pageFilterCtrl: FormControl;
  itemsSubject: ReplaySubject<ListResponse<PageManager>> = new ReplaySubject<ListResponse<PageManager>>(1);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private fileManagerPageService: FileManagerPageService,
    @Inject(DOCUMENT) private document: Document,
    private fb: FormBuilder,
  ) {
    this.pageFilterCtrl = fb.control('');
    this.fileManagerPageService.loadNotCountedList(0, 5, {
    }, this.itemsSubject);
  }

  onChange: any = () => { };
  onTouched: any = () => { };


  ngOnInit(): void {
    if (this.control === null) {
      this.control = new FormControl('');
      this.externalControl = false;
    }

    this.control.valueChanges
      .pipe(
        startWith(this.control.value),
        takeUntil(this.destroyed$)
      )
      .subscribe((val: string) => {
        if (val) {
          let exists = false;
          this.items.forEach((page: PageManager) => {
            if (page.uuid === val) {
              exists = true;
            }
          });

          if (!exists) {
            this.fileManagerPageService.loadNotCountedList(0, 9, {
              in$uuid: val
            }, this.itemsSubject);
          }
        }
      });

    this.pageFilterCtrl.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((val) => {
        if (val) {
          this.fileManagerPageService.loadNotCountedList(0, 5, {
            name: val,
          }, this.itemsSubject);
        } else if (this.control.value) {
          this.fileManagerPageService.loadNotCountedList(0, 9, {
            in$uuid: this.control.value
          }, this.itemsSubject);
        }
      });

    this.itemsSubject
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: ListResponse<PageManager>) => {
          this.items = result.items;
      });
  }
  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (this.externalControl) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    this.items.forEach((page: PageManager) => {
      if (page.uuid === e.value) {
        this.selectedPage.emit(page);
      }
    });

    this.value = e.value;
    this.onChange(this.value);
  }

  clearValue(): void {
    this.control.setValue(this.multiple ? [] : '');
    this.writeValue(this.multiple ? [] : '');
    this.onChange(this.value);
  }
}
